/**
 * AccountabilityGovernancePanel — QST-365
 *
 * Read-only governance summary rendered at the top of non-FBE Accountability
 * sub-tabs. It shows the standardized **Readiness** and **CTO action** label
 * for each known feature so the dashboard can answer:
 *
 *   *"Can this evidence influence live trades yet?"*
 *
 * # Contract
 *
 *   - Read-only. The panel renders the static registry from
 *     ``../lib/promotionGovernance``. It does not query Convex, does not
 *     mutate signals, and does not influence entries / exits / gates / sizing
 *     / alerts / scoring thresholds. The protected FBE forecast surfaces are
 *     intentionally outside this panel.
 *   - No new tab. This panel is mounted ABOVE the existing Accountability
 *     sub-tab content; navigation does not change.
 *   - Conservative labels only. The component must not render anything that
 *     implies automatic promotion or live execution.
 */

import {
  CTO_ACTION_DESCRIPTIONS,
  CTO_ACTION_LABELS,
  READINESS_DESCRIPTIONS,
  READINESS_LABELS,
  selectGovernanceRowsForTab,
  type GovernanceScope,
  type PromotionGovernanceRow,
} from "../lib/promotionGovernance";

interface AccountabilityGovernancePanelProps {
  /** The currently-active Accountability sub-tab key. */
  tab: GovernanceScope;
}

/**
 * Render the standardized governance summary for the given Accountability
 * sub-tab. Always renders the registry rows whose ``scope`` is ``"all"`` or
 * matches the active sub-tab.
 */
export function AccountabilityGovernancePanel({
  tab,
}: AccountabilityGovernancePanelProps) {
  const rows = selectGovernanceRowsForTab(tab);
  return (
    <section
      className="accountability-governance"
      aria-label="Accountability promotion governance"
      data-testid="accountability-governance-panel"
    >
      <header className="accountability-governance-head">
        <div>
          <span className="accountability-governance-kicker">
            QST-365 · Promotion governance
          </span>
          <h3>Can this evidence influence live trades yet?</h3>
          <p>
            Read-only summary. No label here promotes any feature to live
            execution. Entries, exits, gates, sizing, alerts, and scoring
            thresholds are unchanged.
          </p>
        </div>
        <span className="accountability-governance-pill">Read-only</span>
      </header>
      <ul className="accountability-governance-rows" aria-label="Governance rows">
        {rows.map((row) => (
          <GovernanceRow key={row.id} row={row} />
        ))}
      </ul>
      <footer className="accountability-governance-foot">
        <strong>Readiness vocabulary:</strong>{" "}
        Execution-grade · Review candidate · Collecting · Watch-only · Noise /
        rejected.
        <br />
        <strong>CTO actions:</strong>{" "}
        Keep enforced · Keep LOG-ONLY · Collect more · Manual review ·
        Investigate anomaly · Do not promote.
      </footer>
    </section>
  );
}

interface GovernanceRowProps {
  row: PromotionGovernanceRow;
}

function GovernanceRow({ row }: GovernanceRowProps) {
  const readinessLabel = READINESS_LABELS[row.readiness];
  const ctoActionLabel = CTO_ACTION_LABELS[row.ctoAction];
  return (
    <li
      className={`accountability-governance-row accountability-governance-row-${row.readiness}`}
      data-feature-id={row.id}
      data-readiness={row.readiness}
      data-cto-action={row.ctoAction}
    >
      <div className="accountability-governance-row-head">
        <strong>{row.feature}</strong>
        {row.evidenceRef ? (
          <small className="accountability-governance-evidence">
            {row.evidenceRef}
          </small>
        ) : null}
      </div>
      <div className="accountability-governance-row-labels">
        <span
          className={`accountability-governance-readiness accountability-governance-readiness-${row.readiness}`}
          title={READINESS_DESCRIPTIONS[row.readiness]}
        >
          {readinessLabel}
        </span>
        <span
          className={`accountability-governance-action accountability-governance-action-${row.ctoAction}`}
          title={CTO_ACTION_DESCRIPTIONS[row.ctoAction]}
        >
          {ctoActionLabel}
        </span>
      </div>
      <p className="accountability-governance-rationale">{row.rationale}</p>
    </li>
  );
}
